import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';

import { Locker } from '../../../types/api/lockers';
import { LabelValue } from '../../../types/options';
import { emptyCell, noColumnStyle } from '../../../utils/constants/tables';
import { dateCell } from '../../../utils/helpers/dataTable';
import ActionsTableMenu from '../Components/ActionsTableMenu/ActionsTableMenu';

export const tableStorageKey = 'lockers_dataTable';

function getStatus(t: TFunction, status: number) {
  let color;
  let tooltip;

  if (status === 1) {
    color = 'var(--green-500)';
    tooltip = t('Active');
  } else if (status === 0) {
    color = 'var(--red-500)';
    tooltip = t('Inactive');
  } else {
    color = 'var(--gray-500)';
    tooltip = t('Unknown');
  }

  return (
    <span data-tip={tooltip} data-for="tooltip-right">
      <FontAwesomeIcon icon={faCircle} style={{ color }} />
    </span>
  );
}

export function lockerStatuses(t: TFunction): LabelValue[] {
  return [
    { label: t('Active'), value: '1' },
    { label: t('Inactive'), value: '0' },
  ];
}

export function getColumnHeadersMap(t: TFunction) {
  return {
    id: t('ID'),
    title: t('Name'),
    online: t('Status'),
    createdAt: t('Created at'),
    modifiedAt: t('Modified at'),
    address: t('Address'),
    numBoxes: t('Number of boxes'),
    comment: t('Comment'),
  };
}

export function additionalColumnProperties(
  t: TFunction,
  column: keyof ReturnType<typeof getColumnHeadersMap> | 'actions',
  setContextMenuSelection: Dispatch<SetStateAction<any>>,
  setAction: (t: string) => void,
  isViewShown: boolean
) {
  switch (column) {
    case 'id':
      return {
        noColumnStyle,
        style: {
          width: 30,
          maxWidth: 30,
        },
        bodyStyle: {
          width: 30,
          maxWidth: 30,
          display: 'flex',
          justifyContent: 'center',
        },
      };

    case 'online':
      return {
        body: (row: any) => {
          return getStatus(t, row[column]);
        },
        style: { width: 80, maxWidth: 110 },
        bodyStyle: {
          width: 80,
          maxWidth: 110,
          display: 'flex',
          justifyContent: 'center',
        },
      };

    case 'title':
      return {
        style: {
          width: 200,
        },
        bodyStyle: {
          width: 200,
        },
      };

    case 'numBoxes':
      return {
        style: {
          width: 200,
          display: 'flex',
          justifyContent: 'center',
        },
        bodyStyle: {
          width: 200,
          display: 'flex',
          justifyContent: 'center',
        },
      };

    case 'createdAt':
    case 'modifiedAt':
      return {
        body: (row: Locker) => dateCell(row[column] ?? ''),
        style: {
          width: 180,
        },
        bodyStyle: {
          width: 180,
        },
      };

    case 'address':
      return {
        body: (row: any) =>
          row[column]
            ? `${row[column].municipalityName}, ${row[column].rawName}, ${row[column].postcode}`
            : emptyCell,
        style: { width: 200, maxWidth: 200 },
        bodyStyle: {
          width: 200,
          maxWidth: 200,
        },
      };

    case 'comment':
      return {
        body: (row: Locker) =>
          !!row[column]?.trim() ? row[column] : emptyCell,
        style: {
          width: 180,
        },
        bodyStyle: {
          width: 180,
        },
      };

    case 'actions':
      return {
        body: (row: any) => {
          return (
            <ActionsTableMenu
              actions={[
                {
                  label: t('Edit'),
                  icon: 'fas fa-edit',
                  disabled: !isViewShown,
                  command: () => {
                    setContextMenuSelection(row);
                    setAction('edit');
                  },
                },
              ]}
            />
          );
        },
        style: {
          width: 60,
          borderLeft: '1px solid #e9ecef',
          flex: '0 1 auto',
          position: 'sticky',
        },
        bodyStyle: {
          width: 60,
          borderLeft: '1px solid #e9ecef',
          flex: '0 1 auto',
          position: 'sticky',
          justifyContent: 'center',
        },
      };
    default:
      return {};
  }
}
